import { useEffect, useLayoutEffect, useRef, useCallback } from "react";

export function usePersistHorizontalScroll({    
    id = "random-table",
    headRef,          // ref to the header's scrollable element (overflow-x: auto)
    bodyRef,          // ref to the body's scrollable element (e.g., FixedSizeList outerRef)
    deps = [],        // values that change content widths (filters, column widths, etc.)
    debounceMs = 100, // debounce writes to sessionStorage
    enabled = true,   // allow disabling the behavior
}) {

    const key = `hscroll:${id}`;
    const savedXRef = useRef(0);
    const syncingRef = useRef(false);
    const debounceTimeoutRef = useRef(null);

    // ---- helpers ----
    const getMaxScroll = (el) => !el ? 0 : Math.max(0, el.scrollWidth - el.clientWidth);

    const applyScroll = useCallback((el, x) => {
        if (!el) return;
        el.scrollLeft = Math.min(Math.max(0, x), getMaxScroll(el));
    }, []);

    const saveScrollPosition = useCallback((scrollLeft) => {
        if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);

        debounceTimeoutRef.current = setTimeout(() => {
            try {
                sessionStorage.setItem(key, String(scrollLeft));
            } catch (e) {
                console.warn("Failed to save scroll position:", e);
            }
        }, debounceMs);
    }, [key, debounceMs]);

    // Cleanup debounce timer
    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
        };
    }, []);

    // Load saved position (once). Keep it in ref for future re-applies.
    useLayoutEffect(() => {
        if (!enabled) return;
        let saved = 0;
        try {
            const storedValue = sessionStorage.getItem(key);
            saved = storedValue ? Number(storedValue) : 0;
            saved = isFinite(saved) && saved >= 0 ? saved : 0;
        } catch {
            saved = 0;
        }
        savedXRef.current = saved;
    }, [enabled, key]);

    // Apply when refs become available (common with react-window outerRef)
    useLayoutEffect(() => {
        if (!enabled) return;

        const h = headRef?.current;
        const b = bodyRef?.current;
        const x = savedXRef.current;

        applyScroll(h, x);
        applyScroll(b, x);

        // Apply again next frame in case layout finalizes after paint
        const raf = requestAnimationFrame(() => {
            applyScroll(headRef?.current, x);
            applyScroll(bodyRef?.current, x);
        });
        return () => cancelAnimationFrame(raf);
        // re-run when the actual DOM nodes attach
    }, [enabled, headRef, bodyRef, applyScroll]);

    // Re-apply when content-affecting deps change (e.g., columns resize, filters)
    useLayoutEffect(() => {
        if (!enabled || deps.length === 0) return;
        const x = savedXRef.current;
        applyScroll(headRef?.current, x);
        applyScroll(bodyRef?.current, x);
    }, [applyScroll, bodyRef, deps.length, enabled, headRef]);

    // Keep header & body in sync and persist value
    useEffect(() => {
        if (!enabled) return;

        const h = headRef?.current;
        const b = bodyRef?.current;
        if (!h || !b) return;

        const createScrollHandler = (source, target) => () => {
            if (syncingRef.current) return;
            syncingRef.current = true;
            const x = source.scrollLeft;
            applyScroll(target, x);
            savedXRef.current = x;
            saveScrollPosition(x);
            syncingRef.current = false;
        };

        const onHeadScroll = createScrollHandler(h, b);
        const onBodyScroll = createScrollHandler(b, h);

        h.addEventListener("scroll", onHeadScroll, { passive: true });
        b.addEventListener("scroll", onBodyScroll, { passive: true });

        return () => {
            h.removeEventListener("scroll", onHeadScroll);
            b.removeEventListener("scroll", onBodyScroll);
        };

    }, [enabled, headRef, bodyRef, saveScrollPosition, applyScroll]);

    // Re-apply on container size changes (e.g., window resize, column width changes)
    useEffect(() => {
        if (!enabled) return;

        const els = [headRef?.current, bodyRef?.current].filter(Boolean);
        if (!els.length || typeof ResizeObserver === "undefined") return;

        const ro = new ResizeObserver(() => {
            const x = savedXRef.current;
            els.forEach((el) => applyScroll(el, x));
        });

        els.forEach((el) => ro.observe(el));
        return () => ro.disconnect();

    }, [enabled, headRef, bodyRef, applyScroll]);

    // ---- public API ----
    const scrollToPosition = useCallback((x) => {
        if (!enabled) return;

        // choose a target to clamp against (body preferred if present)
        const target = bodyRef?.current || headRef?.current;
        const max = getMaxScroll(target);
        const clamped = Math.max(0, Math.min(x ?? 0, max));

        savedXRef.current = clamped;
        applyScroll(headRef?.current, clamped);
        applyScroll(bodyRef?.current, clamped);
        saveScrollPosition(clamped);

    }, [enabled, bodyRef, headRef, applyScroll, saveScrollPosition]);

    const resetScroll = useCallback(() => {
        if (!enabled) return;
        savedXRef.current = 0;
        applyScroll(headRef?.current, 0);
        applyScroll(bodyRef?.current, 0);
        try {
            sessionStorage.removeItem(key);
        } catch (e) {
            console.warn("Failed to remove scroll position:", e);
        }
    }, [enabled, applyScroll, headRef, bodyRef, key]);

    return { scrollToPosition, resetScroll };
}